/**
 * Ticket domain engine (PRD "Status workflow", "ETA and SLA", "Activity tab"). Pure functions,
 * no IO: each takes a ticket plus a context and returns the next ticket and the activity entries
 * to append. Callers persist both together. Every mutation bumps `updatedAt`; the store bumps
 * `version`.
 */
import {
  type ConfigLists,
  findItem,
  firstActiveInStage,
  type Priority,
  type Stage,
  type Status,
  stageOf,
} from '../config.ts'
import type { AccessLevel } from '../roles.ts'
import {
  DAY_MS,
  DEFAULT_ORG_TIMEZONE,
  fmtDuration,
  HOUR_MS,
  parseIsoDate,
  zonedTimeToUtc,
} from './time.ts'

export const ACTIVITY_TYPES = [
  'created',
  'status',
  'assignee',
  'priority',
  'category',
  'eta',
  'confidential',
  'reopened',
  /** Files added after the ticket was raised; `toValue` = attachment ids, comma separated. */
  'attachment',
] as const
export type ActivityType = (typeof ACTIVITY_TYPES)[number]

/** Waits shorter than this aren't mentioned in the activity log. */
export const PAUSE_LOG_MIN_MS = HOUR_MS
/** Manual ETAs land at this local hour on the picked day. */
export const MANUAL_ETA_HOUR = 18
/** Statuses the engine prefers by id (stable ids from the PRD defaults). */
export const REOPENED_STATUS_ID = 'reopened'
export const IN_PROGRESS_STATUS_ID = 'progress'

/** The persisted fields of a ticket (times are epoch ms). Ids reference config and people. */
export interface TicketFields {
  title: string
  description: string
  categoryId: string
  priorityId: string
  /** The employee's original choice. */
  requestedPriorityId: string
  statusId: string
  /** Where the ticket returns when the employee replies on a Waiting ticket. */
  prevStatusId: string | null
  raisedBy: string
  loggedBy: string
  assignee: string | null
  confidential: boolean
  createdAt: number
  updatedAt: number
  dueAt: number | null
  etaManual: boolean
  resolvedAt: number | null
  waitingSince: number | null
}

/** Extra details on an activity entry. */
export interface ActivityMeta {
  /** Status change caused by the employee replying on a Waiting ticket. */
  auto?: boolean
  /** Time spent waiting (ms), recorded when ≥ 1h and the ticket didn't go to Done. */
  paused?: number
  /** ETA entry: the ETA was reset to the SLA (otherwise it was set manually). */
  reset?: boolean
}

/** An activity entry before it is stored. Values are ids (or epoch ms as text for ETAs). */
export interface ActivityDraft {
  type: ActivityType
  actorId: string
  fromValue: string | null
  toValue: string | null
  meta: ActivityMeta | null
  at: number
}

export interface EngineContext {
  now: number
  /** Who is making the change. */
  actorId: string
  config: Pick<ConfigLists, 'priorities' | 'statuses'>
}

export interface EngineResult<T extends TicketFields> {
  ticket: T
  activity: ActivityDraft[]
}

/** Thrown for inputs the engine can't apply (unknown status, reopening an open ticket…). */
export class TicketRuleError extends Error {
  constructor(
    readonly code:
      | 'unknown_status'
      | 'unknown_priority'
      | 'unknown_category'
      | 'not_done'
      | 'invalid_date'
      | 'no_status',
    message: string,
  ) {
    super(message)
  }
}

/** Builds one activity entry. The only way entries are created, so their shape stays uniform. */
export function activityEntry(
  type: ActivityType,
  actorId: string,
  at: number,
  fromValue: string | null = null,
  toValue: string | null = null,
  meta: ActivityMeta | null = null,
): ActivityDraft {
  const cleanMeta = meta && Object.keys(meta).length > 0 ? meta : null
  return { type, actorId, at, fromValue, toValue, meta: cleanMeta }
}

// ---------------------------------------------------------------------------------------------
// ETA

/** Rule 1: created time + the priority's SLA (calendar days). Unknown priority → no ETA. */
export function computeDueAt(createdAt: number, priority: Priority | undefined): number | null {
  if (!priority) return null
  return createdAt + Math.round(priority.slaDays * DAY_MS)
}

function priorityOf(ctx: EngineContext, id: string) {
  return findItem(ctx.config.priorities, id)
}

/** "6 pm that day" in the org time zone for a `YYYY-MM-DD` date. */
export function manualEtaAt(date: string, timeZone: string = DEFAULT_ORG_TIMEZONE): number {
  const d = parseIsoDate(date)
  if (!d) throw new TicketRuleError('invalid_date', 'Pick a valid date.')
  return zonedTimeToUtc(d.year, d.month, d.day, MANUAL_ETA_HOUR, 0, timeZone)
}

/** Overdue: past the ETA and not in the Done or Waiting stage. */
export function isOverdue(
  ticket: Pick<TicketFields, 'dueAt' | 'statusId'>,
  now: number,
  statuses: readonly Status[],
): boolean {
  if (ticket.dueAt === null) return false
  const stage = stageOf(statuses, ticket.statusId)
  if (stage === 'done' || stage === 'waiting') return false
  return now > ticket.dueAt
}

/** Whole days late, rounded, minimum 1 ("Overdue 3d"). */
export function overdueDays(ticket: Pick<TicketFields, 'dueAt'>, now: number): number {
  if (ticket.dueAt === null) return 0
  return Math.max(1, Math.round((now - ticket.dueAt) / DAY_MS))
}

export type EtaViewer = 'employee' | 'hr'

export type EtaDisplay =
  | { kind: 'done'; label: string; resolvedAt: number | null }
  | { kind: 'paused'; label: string }
  | { kind: 'overdue'; label: string; days: number; dueAt: number; manual: boolean }
  | {
      kind: 'due'
      dueAt: number
      manual: boolean
      /** Where the date came from when not manual (for "From Medium SLA (3d)"). */
      priority: Priority | undefined
    }
  | { kind: 'none'; label: string }

/** Viewer for `etaDisplay` from a PRD access level (viewers see the HR wording). */
export function etaViewerFor(level: AccessLevel): EtaViewer {
  return level === 'employee' ? 'employee' : 'hr'
}

/** What the ETA field shows: done, paused, overdue, a due date, or nothing. */
export function etaDisplay(
  ticket: Pick<TicketFields, 'dueAt' | 'statusId' | 'etaManual' | 'resolvedAt' | 'priorityId'>,
  opts: { now: number; config: Pick<ConfigLists, 'priorities' | 'statuses'>; viewer: EtaViewer },
): EtaDisplay {
  const stage = stageOf(opts.config.statuses, ticket.statusId)
  if (stage === 'done') {
    const name = findItem(opts.config.statuses, ticket.statusId)?.name ?? 'Resolved'
    return { kind: 'done', label: name, resolvedAt: ticket.resolvedAt }
  }
  if (stage === 'waiting')
    return {
      kind: 'paused',
      label: opts.viewer === 'employee' ? 'Paused until you reply' : 'Paused',
    }
  if (ticket.dueAt === null) return { kind: 'none', label: 'No date set' }
  if (isOverdue(ticket, opts.now, opts.config.statuses)) {
    const days = overdueDays(ticket, opts.now)
    return {
      kind: 'overdue',
      label: `Overdue ${days}d`,
      days,
      dueAt: ticket.dueAt,
      manual: ticket.etaManual,
    }
  }
  return {
    kind: 'due',
    dueAt: ticket.dueAt,
    manual: ticket.etaManual,
    priority: findItem(opts.config.priorities, ticket.priorityId),
  }
}

/** "ETA pushed out by 1.3d for the time spent waiting" suffix, or null. */
export function pausedNote(meta: ActivityMeta | null | undefined): string | null {
  if (!meta?.paused || meta.paused < PAUSE_LOG_MIN_MS) return null
  return `ETA pushed out by ${fmtDuration(meta.paused)} for the time spent waiting`
}

// ---------------------------------------------------------------------------------------------
// Creation

export interface NewTicketInput {
  title: string
  description: string
  categoryId: string
  priorityId: string
  confidential: boolean
  /** The employee the ticket is about. */
  raisedBy: string
  /** Who created it (differs from raisedBy when HR logs it on someone's behalf). */
  loggedBy: string
}

/**
 * A new ticket: first active New status, ETA from the SLA, requested priority = priority,
 * unassigned, plus a `created` entry by the person who logged it.
 */
export function createTicket(
  input: NewTicketInput,
  ctx: Omit<EngineContext, 'actorId'>,
): EngineResult<TicketFields> {
  const status = firstActiveInStage(ctx.config.statuses, 'new')
  if (!status) throw new TicketRuleError('no_status', 'There is no active status in the New stage.')
  const priority = findItem(ctx.config.priorities, input.priorityId)
  if (!priority) throw new TicketRuleError('unknown_priority', 'Unknown priority.')
  const ticket: TicketFields = {
    title: input.title,
    description: input.description,
    categoryId: input.categoryId,
    priorityId: input.priorityId,
    requestedPriorityId: input.priorityId,
    statusId: status.id,
    prevStatusId: null,
    raisedBy: input.raisedBy,
    loggedBy: input.loggedBy,
    assignee: null,
    confidential: input.confidential,
    createdAt: ctx.now,
    updatedAt: ctx.now,
    dueAt: computeDueAt(ctx.now, priority),
    etaManual: false,
    resolvedAt: null,
    waitingSince: null,
  }
  return { ticket, activity: [activityEntry('created', input.loggedBy, ctx.now)] }
}

// ---------------------------------------------------------------------------------------------
// Status

/**
 * Moves a ticket to `toStatusId` (any known status; HR pickers offer active ones only).
 * - Entering Done records `resolvedAt` (kept when moving between Done statuses); leaving clears it.
 * - Entering Waiting records `waitingSince` and `prevStatusId`.
 * - Leaving Waiting for a non-Done status pushes `dueAt` out by the time spent waiting; the
 *   activity entry records `paused` when it was at least an hour. Waiting → Done: no extension.
 * Same status → no change and no activity.
 */
export function applyStatusChange<T extends TicketFields>(
  ticket: T,
  toStatusId: string,
  ctx: EngineContext & { auto?: boolean },
): EngineResult<T> {
  if (toStatusId === ticket.statusId) return { ticket, activity: [] }
  const toStage = stageOf(ctx.config.statuses, toStatusId)
  if (!toStage) throw new TicketRuleError('unknown_status', 'Unknown status.')
  const fromStage: Stage | undefined = stageOf(ctx.config.statuses, ticket.statusId)

  const next: T = { ...ticket, statusId: toStatusId, updatedAt: ctx.now }
  const meta: ActivityMeta = {}
  if (ctx.auto) meta.auto = true

  if (toStage !== 'done') next.resolvedAt = null
  else if (fromStage !== 'done' || ticket.resolvedAt === null) next.resolvedAt = ctx.now

  if (fromStage === 'waiting' && toStage !== 'waiting') {
    const paused = ticket.waitingSince === null ? 0 : Math.max(0, ctx.now - ticket.waitingSince)
    if (toStage !== 'done') {
      if (ticket.dueAt !== null && paused > 0) next.dueAt = ticket.dueAt + paused
      if (paused >= PAUSE_LOG_MIN_MS) meta.paused = paused
    }
    next.waitingSince = null
  }
  if (toStage === 'waiting' && fromStage !== 'waiting') {
    next.waitingSince = ctx.now
    next.prevStatusId = ticket.statusId
  }

  return {
    ticket: next,
    activity: [activityEntry('status', ctx.actorId, ctx.now, ticket.statusId, toStatusId, meta)],
  }
}

const resumable = (s: Status | undefined): s is Status =>
  !!s && !s.archived && s.stage !== 'waiting' && s.stage !== 'done'

/**
 * Where a Waiting ticket goes when the employee replies: the status before waiting if it is
 * active and not Waiting/Done; else In progress; else the first In progress-stage status; else
 * the first New status. Undefined only when no such status exists.
 */
export function resumeTarget(
  prevStatusId: string | null,
  statuses: readonly Status[],
): string | undefined {
  const prev = findItem(statuses, prevStatusId)
  if (resumable(prev)) return prev.id
  const progress = findItem(statuses, IN_PROGRESS_STATUS_ID)
  if (resumable(progress)) return progress.id
  return (firstActiveInStage(statuses, 'in_progress') ?? firstActiveInStage(statuses, 'new'))?.id
}

/**
 * The employee replied on a Waiting ticket: back to `resumeTarget`, with `auto` on the entry.
 * Returns the ticket unchanged (no activity) when it isn't waiting.
 */
export function resumeFromWaiting<T extends TicketFields>(
  ticket: T,
  ctx: EngineContext,
): EngineResult<T> & { resumed: boolean } {
  if (stageOf(ctx.config.statuses, ticket.statusId) !== 'waiting')
    return { ticket, activity: [], resumed: false }
  const target = resumeTarget(ticket.prevStatusId, ctx.config.statuses)
  if (!target) return { ticket, activity: [], resumed: false }
  return { ...applyStatusChange(ticket, target, { ...ctx, auto: true }), resumed: true }
}

/** Status a reopened ticket moves to: Reopened if active, else the first New status. */
export function reopenTarget(statuses: readonly Status[]): string | undefined {
  const reopened = findItem(statuses, REOPENED_STATUS_ID)
  if (reopened && !reopened.archived && reopened.stage === 'new') return reopened.id
  return firstActiveInStage(statuses, 'new')?.id
}

/**
 * Employee reopens a Done ticket: Reopened (or first New), resolvedAt cleared, fresh ETA of
 * now + SLA unless the ETA was set by hand. One `reopened` entry (from/to status ids).
 */
export function reopen<T extends TicketFields>(ticket: T, ctx: EngineContext): EngineResult<T> {
  if (stageOf(ctx.config.statuses, ticket.statusId) !== 'done')
    throw new TicketRuleError('not_done', 'Only resolved or closed tickets can be reopened.')
  const target = reopenTarget(ctx.config.statuses)
  if (!target) throw new TicketRuleError('no_status', 'There is no active status in the New stage.')
  const next: T = {
    ...ticket,
    statusId: target,
    resolvedAt: null,
    waitingSince: null,
    updatedAt: ctx.now,
    dueAt: ticket.etaManual
      ? ticket.dueAt
      : computeDueAt(ctx.now, priorityOf(ctx, ticket.priorityId)),
  }
  return {
    ticket: next,
    activity: [activityEntry('reopened', ctx.actorId, ctx.now, ticket.statusId, target)],
  }
}

// ---------------------------------------------------------------------------------------------
// Other HR edits (Wave 4 uses these)

/** Priority change; the ETA is recalculated from the created time unless set by hand. */
export function changePriority<T extends TicketFields>(
  ticket: T,
  priorityId: string,
  ctx: EngineContext,
): EngineResult<T> {
  if (priorityId === ticket.priorityId) return { ticket, activity: [] }
  const priority = priorityOf(ctx, priorityId)
  if (!priority) throw new TicketRuleError('unknown_priority', 'Unknown priority.')
  const next: T = { ...ticket, priorityId, updatedAt: ctx.now }
  if (!ticket.etaManual) next.dueAt = computeDueAt(ticket.createdAt, priority)
  return {
    ticket: next,
    activity: [activityEntry('priority', ctx.actorId, ctx.now, ticket.priorityId, priorityId)],
  }
}

/** HR picks a date: ETA becomes 6 pm that day (org time zone) and is marked manual. */
export function setManualEta<T extends TicketFields>(
  ticket: T,
  date: string,
  ctx: EngineContext & { timeZone?: string },
): EngineResult<T> {
  const dueAt = manualEtaAt(date, ctx.timeZone)
  if (ticket.etaManual && ticket.dueAt === dueAt) return { ticket, activity: [] }
  const next: T = { ...ticket, dueAt, etaManual: true, updatedAt: ctx.now }
  const from = ticket.dueAt === null ? null : String(ticket.dueAt)
  return {
    ticket: next,
    activity: [activityEntry('eta', ctx.actorId, ctx.now, from, String(dueAt))],
  }
}

/** "Reset to SLA": manual flag off, ETA = created time + SLA. */
export function resetEtaToSla<T extends TicketFields>(
  ticket: T,
  ctx: EngineContext,
): EngineResult<T> {
  if (!ticket.etaManual) return { ticket, activity: [] }
  const dueAt = computeDueAt(ticket.createdAt, priorityOf(ctx, ticket.priorityId))
  const next: T = { ...ticket, dueAt, etaManual: false, updatedAt: ctx.now }
  const from = ticket.dueAt === null ? null : String(ticket.dueAt)
  const to = dueAt === null ? null : String(dueAt)
  return {
    ticket: next,
    activity: [activityEntry('eta', ctx.actorId, ctx.now, from, to, { reset: true })],
  }
}

/** Assign (or unassign with null). */
export function changeAssignee<T extends TicketFields>(
  ticket: T,
  assignee: string | null,
  ctx: EngineContext,
): EngineResult<T> {
  if (assignee === ticket.assignee) return { ticket, activity: [] }
  return {
    ticket: { ...ticket, assignee, updatedAt: ctx.now },
    activity: [activityEntry('assignee', ctx.actorId, ctx.now, ticket.assignee, assignee)],
  }
}

export function changeCategory<T extends TicketFields>(
  ticket: T,
  categoryId: string,
  ctx: EngineContext,
): EngineResult<T> {
  if (categoryId === ticket.categoryId) return { ticket, activity: [] }
  return {
    ticket: { ...ticket, categoryId, updatedAt: ctx.now },
    activity: [activityEntry('category', ctx.actorId, ctx.now, ticket.categoryId, categoryId)],
  }
}

export function setConfidential<T extends TicketFields>(
  ticket: T,
  confidential: boolean,
  ctx: EngineContext,
): EngineResult<T> {
  if (confidential === ticket.confidential) return { ticket, activity: [] }
  return {
    ticket: { ...ticket, confidential, updatedAt: ctx.now },
    activity: [
      activityEntry(
        'confidential',
        ctx.actorId,
        ctx.now,
        String(ticket.confidential),
        String(confidential),
      ),
    ],
  }
}

/** A comment bumps `updatedAt` (no activity entry; comments have their own list). */
export function touch<T extends TicketFields>(ticket: T, now: number): T {
  return { ...ticket, updatedAt: now }
}

// ---------------------------------------------------------------------------------------------
// Keys

export const TICKET_KEY_PREFIX = 'HR-'

/** `HR-1001` for id 1001. */
export function ticketKey(id: number): string {
  return `${TICKET_KEY_PREFIX}${id}`
}

/** Accepts `HR-1001`, `hr-1001` or `1001`; null for anything else. */
export function parseTicketKey(key: string): number | null {
  const m = /^(?:hr-)?(\d{1,9})$/i.exec(key.trim())
  if (!m) return null
  const id = Number(m[1])
  return Number.isSafeInteger(id) && id > 0 ? id : null
}
